import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, Truck } from 'lucide-react';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { TrustBadge } from '../components/ui/TrustBadge';
import { useCart } from '../hooks/useCart';
export function CartPage() {
  const {
    items,
    subtotal,
    deliveryFee,
    totalPrice,
    updateQuantity,
    removeFromCart,
    clearCart
  } = useCart();
  const freeDeliveryThreshold = 35;
  const amountToFreeDelivery = Math.max(0, freeDeliveryThreshold - subtotal);
  return <div className="min-h-screen bg-[#FAF8F5] pb-20 md:pb-0" data-id="element-236">
      <TopNav data-id="element-237" />

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8" data-id="element-238">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" data-id="element-239">
          <div className="flex items-center gap-4" data-id="element-240">
            <Link to="/search" className="p-2 hover:bg-white rounded-lg transition-colors" aria-label="Continue shopping" data-id="element-241">
              <ArrowLeft className="w-5 h-5 text-gray-600" data-id="element-242" />
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-243">
              Your Cart
            </h1>
          </div>
          {items.length > 0 && <button onClick={clearCart} className="text-sm text-gray-500 hover:text-[#D97757] font-medium transition-colors" data-id="element-244">
              Clear Cart
            </button>}
        </div>

        {items.length > 0 ? <div className="grid md:grid-cols-3 gap-6" data-id="element-245">
            {/* Cart Items */}
            <div className="md:col-span-2 space-y-4" data-id="element-246">
              {/* Free Delivery Progress */}
              {amountToFreeDelivery > 0 && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-gradient-to-r from-[#948B7A] to-[#7d7465] text-white p-4 rounded-xl" data-id="element-247">
                  <div className="flex items-center gap-3 mb-2" data-id="element-248">
                    <Truck className="w-5 h-5" data-id="element-249" />
                    <span className="font-semibold" data-id="element-250">
                      Add ${amountToFreeDelivery.toFixed(2)} more for FREE
                      delivery!
                    </span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-251">
                    <motion.div initial={{
                width: 0
              }} animate={{
                width: `${subtotal / freeDeliveryThreshold * 100}%`
              }} className="h-full bg-white rounded-full" transition={{
                duration: 0.5
              }} data-id="element-252" />
                  </div>
                </motion.div>}

              {/* Items List */}
              <AnimatePresence mode="popLayout" data-id="element-253">
                {items.map(item => <motion.div key={item.product.id} layout initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95,
              x: -100
            }} transition={{
              duration: 0.2
            }} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-id="element-254">
                    <div className="flex gap-4" data-id="element-255">
                      {/* Image */}
                      <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-256">
                        <img src={item.product.image} alt={item.product.altText} className="w-full h-full object-cover" data-id="element-257" />
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0" data-id="element-258">
                        <div className="flex items-start justify-between gap-2" data-id="element-259">
                          <div data-id="element-260">
                            <h3 className="font-bold text-gray-900 line-clamp-1" data-id="element-261">
                              {item.product.name}
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5" data-id="element-262">
                              {item.product.category}
                            </p>
                            <div className="flex gap-1.5 mt-2" data-id="element-263">
                              {item.product.isWIC && <TrustBadge type="WIC" size="sm" data-id="element-264" />}
                              {item.product.isEBT && <TrustBadge type="EBT" size="sm" data-id="element-265" />}
                            </div>
                          </div>
                          <button onClick={() => removeFromCart(item.product.id)} className="p-2 text-gray-400 hover:text-[#D97757] hover:bg-red-50 rounded-lg transition-colors" aria-label={`Remove ${item.product.name} from cart`} data-id="element-266">
                            <Trash2 className="w-5 h-5" data-id="element-267" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between mt-4" data-id="element-268">
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-2" data-id="element-269">
                            <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors" aria-label="Decrease quantity" data-id="element-270">
                              <Minus className="w-4 h-4" data-id="element-271" />
                            </button>
                            <span className="w-8 text-center font-semibold" data-id="element-272">
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors" aria-label="Increase quantity" data-id="element-273">
                              <Plus className="w-4 h-4" data-id="element-274" />
                            </button>
                          </div>

                          {/* Price */}
                          <span className="text-lg font-bold text-[#2D5F3F]" data-id="element-275">
                            ${(item.product.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
            </div>

            {/* Order Summary */}
            <div className="md:col-span-1" data-id="element-276">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24" data-id="element-277">
                <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-278">
                  Order Summary
                </h2>

                <div className="space-y-3 mb-6" data-id="element-279">
                  <div className="flex justify-between text-sm" data-id="element-280">
                    <span className="text-gray-600" data-id="element-281">Subtotal</span>
                    <span className="font-medium" data-id="element-282">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-283">
                    <span className="text-gray-600" data-id="element-284">Delivery</span>
                    <span className={`font-medium ${deliveryFee === 0 ? 'text-[#047857]' : ''}`} data-id="element-285">
                      {deliveryFee === 0 ? 'FREE' : `$${deliveryFee.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 flex justify-between" data-id="element-286">
                    <span className="font-bold text-gray-900" data-id="element-287">Total</span>
                    <span className="text-xl font-bold text-[#2D5F3F]" data-id="element-288">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button className="w-full bg-[#948B7A] hover:bg-[#7d7465] text-white font-bold py-4 rounded-xl transition-colors mb-4" data-id="element-289">
                  Proceed to Checkout
                </button>

                <Link to="/search" className="block text-center text-sm text-[#948B7A] font-semibold hover:underline" data-id="element-290">
                  Continue Shopping
                </Link>

                {/* Payment Methods */}
                <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-291">
                  <p className="text-xs text-gray-500 text-center mb-3" data-id="element-292">
                    We accept
                  </p>
                  <div className="flex justify-center gap-2" data-id="element-293">
                    <TrustBadge type="WIC" size="sm" data-id="element-294" />
                    <TrustBadge type="EBT" size="sm" data-id="element-295" />
                  </div>
                </div>
              </div>
            </div>
          </div> /* Empty Cart State */ : <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center py-16" data-id="element-296">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-297">
              <ShoppingBag className="w-12 h-12 text-gray-400" data-id="element-298" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-299">
              Your cart is empty
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto" data-id="element-300">
              Looks like you haven't added any items to your cart yet. Start
              shopping to fill it up!
            </p>
            <Link to="/search" className="inline-flex items-center gap-2 bg-[#948B7A] hover:bg-[#7d7465] text-white px-8 py-4 rounded-xl font-bold transition-colors" data-id="element-301">
              <ShoppingBag className="w-5 h-5" data-id="element-302" />
              Start Shopping
            </Link>
          </motion.div>}
      </div>

      <BottomNav data-id="element-303" />
    </div>;
}